import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalBase from "../ModalBase";
import ChargingMetricCurve from "../../components/chargeMonitor/ChargingMetricCurve";
import PointStatusLog from "../../components/point/PointStatusLog";
import SessionDetailsSummary from "../session/SessionDetailsSummary";
import LoadingSpinner from "../LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faTable } from "@fortawesome/pro-regular-svg-icons";
import { useAuth, Roles } from "../../providers/AuthContext"


const ChargingDetailsModal = ({
	isDetailsOpen,
	setIsDetailsOpen,
	selectedSessionId,
	selectedConnectorId,
	selectedPointId,
	startDateTimeUtc,
	endDateTimeUtc,
	canViewSessionDetails = true,
}) => {
	const [activeTab, setActiveTab] = useState("metrics");
	const [chartsLoading, setChartsLoading] = useState(true);
	const [detailsLoading, setDetailsLoading] = useState(true);
	const { hasRole, hasAnyRole } = useAuth();
	const customerLevelDetails = hasRole(Roles.CpmsLight) && canViewSessionDetails === false;
	const canLoadSessionDetails = hasAnyRole([Roles.Admin, Roles.CustomerService, Roles.CpmsLight, Roles.Customer, Roles.Partner]);
	const showStatusLogs = !hasRole(Roles.Partner) && !hasRole(Roles.Customer) && !customerLevelDetails;
	const { t } = useTranslation();

	const metricsLoading = chartsLoading || (canLoadSessionDetails && detailsLoading);

	useEffect(() => {
		if (!isDetailsOpen) {
			setChartsLoading(true);
			setDetailsLoading(true);
			return;
		}

		setChartsLoading(true);
		setDetailsLoading(true);
		if (customerLevelDetails) {
			setActiveTab("metrics");
		}
	}, [isDetailsOpen, selectedSessionId, customerLevelDetails]);

	let dateFrom = null;
	let dateTo = null;

	if (startDateTimeUtc) {
		const start = new Date(startDateTimeUtc);
		start.setMinutes(start.getMinutes() - 30);
		dateFrom = start.toISOString();
	}

	if (endDateTimeUtc) {
		const end = new Date(endDateTimeUtc);
		end.setMinutes(end.getMinutes() + 30);
		dateTo = end.toISOString();
	}


	return (
		<ModalBase
			isOpen={isDetailsOpen}
			onClose={() => setIsDetailsOpen(false)}
			size="xl"
			className="rounded-3xl overflow-hidden flex flex-col"
		>
			<div className="border-b border-mute-200 flex space-x-8">
				<button
					onClick={() => setActiveTab("metrics")}
					className={`flex items-center gap-2 pb-2 transition-colors ${activeTab === "metrics"
							? "border-b-2 border-black font-bold text-sm text-black"
							: "text-mute-400 text-sm font-bold"
						}`}
				>
					<FontAwesomeIcon icon={faChartLine} className="w-4 h-4" />
					{t("chargeMonitor.metrics")}
				</button>
				{showStatusLogs && (
					<button
						onClick={() => setActiveTab("logs")}
						className={`flex items-center gap-2 pb-2 transition-colors ${activeTab === "logs"
							? "border-b-2 border-black font-bold text-sm text-black"
							: "text-mute-400 text-sm font-bold"
							}`}
					>
						<FontAwesomeIcon icon={faTable} className="w-4 h-4" />
						{t("chargeMonitor.statusLogs")}
					</button>
				)}
			</div>


			<div className="flex-1 overflow-y-auto px-2 py-2">
				{activeTab === "metrics" || !showStatusLogs ? (
					<>
						{metricsLoading && (
							<div className="flex justify-center py-16">
								<LoadingSpinner text={t("chargeMonitor.loadingSessionDetails")} />
							</div>
						)}
						<div className={metricsLoading ? "hidden" : "flex flex-col gap-4"}>
							<ChargingMetricCurve
								chargingSessionPublicId={selectedSessionId}
								isOpen={isDetailsOpen}
								useSse={false}
								showSpinner={false}
								onLoadingChange={setChartsLoading}
							/>
							{canLoadSessionDetails && (
								<SessionDetailsSummary
									chargingSessionPublicId={selectedSessionId}
									isOpen={isDetailsOpen}
									showSpinner={false}
									onLoadingChange={setDetailsLoading}
								/>
							)}
						</div>
					</>
				) : (
					<PointStatusLog
							isModal={true}
							useSse={false}
							pointPublicId={selectedPointId}
							connectorPublicId={selectedConnectorId}
							dateFrom={dateFrom}
							dateTo={dateTo}
							sessionTime={startDateTimeUtc}
					/>
				)}
			</div>
		</ModalBase>
	);
};

export default ChargingDetailsModal;
